import { api, API_ENDPOINTS } from './api';

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data?: T;
  timestamp: string;
  errorCode?: string;
}

export interface LinkChildRequest {
  childId: number;
  parentId: number;
}

export interface ChildSearchMinimalResult {
  id: number;
  initials: string;
}

export interface ChildSearchResult {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  schoolName: string;
  gradeLevel: string;
  dateOfBirth: string;
  birthYear?: number;
  userType: string;
  status: string;
}

export interface LinkRequestResponse {
  relationshipId: number;
  message: string;
  alreadyLinked: boolean;
}

export interface PendingLinkRequest {
  relationshipId: number;
  childId: number;
  childEmail: string;
  initials: string;
  status: string;
  codeExpired: boolean;
  locked: boolean;
}

export interface ChildRegistrationRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phoneNumber?: string;
  dateOfBirth: string;
  birthYear?: number;
  schoolName: string;
  gradeLevel: string;
  parentUserId: number;
  councilId?: number;
  councilName?: string;
  /** True → create UNKNOWN_SCHOOL report after registration (councilId must be provided). */
  unknownSchool?: boolean;
  /** Free-text council name → create UNKNOWN_COUNCIL report after registration. */
  unknownCouncilName?: string;
}

class ChildManagementService {
  async searchChildByEmail(email: string): Promise<ChildSearchMinimalResult> {
    try {
      const response = await api.get<ApiResponse<ChildSearchMinimalResult>>(
        `${API_ENDPOINTS.CHILD_MANAGEMENT.SEARCH}?email=${encodeURIComponent(email)}`,
        { preventAuthRedirect: true } as any
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to search for child');
    } catch (error: any) {
      console.error('Error searching for child:', error);
      if (error.response?.status === 401) {
        throw new Error('Your session has expired. Please log in again to search for children.');
      }
      throw new Error(
        error.response?.data?.message ||
        error.message ||
        'Failed to search for child'
      );
    }
  }

  async requestChildLink(childId: number, parentId: number): Promise<LinkRequestResponse> {
    try {
      const response = await api.post<ApiResponse<LinkRequestResponse>>(
        API_ENDPOINTS.CHILD_MANAGEMENT.LINK,
        { childId, parentId }
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to request child link');
    } catch (error: any) {
      console.error('Error requesting child link:', error);
      throw new Error(
        error.response?.data?.message ||
        error.message ||
        'Failed to request child link'
      );
    }
  }

  async verifyLinkCode(relationshipId: number, code: string): Promise<void> {
    try {
      const response = await api.post<ApiResponse<void>>(
        API_ENDPOINTS.CHILD_MANAGEMENT.LINK_VERIFY,
        { relationshipId, code }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to verify code');
      }
    } catch (error: any) {
      console.error('Error verifying link code:', error);
      throw new Error(
        error.response?.data?.message ||
        error.message ||
        'Failed to verify code'
      );
    }
  }

  async resendLinkCode(relationshipId: number): Promise<void> {
    try {
      const response = await api.post<ApiResponse<void>>(
        API_ENDPOINTS.CHILD_MANAGEMENT.LINK_RESEND,
        { relationshipId }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to resend code');
      }
    } catch (error: any) {
      console.error('Error resending link code:', error);
      throw new Error(
        error.response?.data?.message ||
        error.message ||
        'Failed to resend code'
      );
    }
  }

  async getPendingLinkRequests(): Promise<PendingLinkRequest[]> {
    try {
      const response = await api.get<ApiResponse<PendingLinkRequest[]>>(
        API_ENDPOINTS.CHILD_MANAGEMENT.LINK_PENDING
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to load pending requests');
    } catch (error: any) {
      console.error('Error loading pending link requests:', error);
      throw new Error(
        error.response?.data?.message ||
        error.message ||
        'Failed to load pending requests'
      );
    }
  }

  /** @deprecated Use requestChildLink + verifyLinkCode */
  async linkChildToParent(childId: number, parentId: number): Promise<LinkRequestResponse> {
    return this.requestChildLink(childId, parentId);
  }

  async registerChild(childData: ChildRegistrationRequest): Promise<ChildSearchResult> {
    try {
      const response = await api.post<ApiResponse<ChildSearchResult>>(
        API_ENDPOINTS.CHILD_MANAGEMENT.REGISTER,
        childData
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to register child');
    } catch (error: any) {
      console.error('Error registering child:', error);
      throw new Error(
        error.response?.data?.message ||
        error.message ||
        'Failed to register child'
      );
    }
  }

  async getLinkedChildren(parentId: number): Promise<ChildSearchResult[]> {
    try {
      const response = await api.get<ApiResponse<ChildSearchResult[]>>(
        `${API_ENDPOINTS.CHILD_MANAGEMENT.LINKED_CHILDREN}?parentId=${parentId}`
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to get linked children');
    } catch (error: any) {
      console.error('Error getting linked children:', error);
      throw new Error(
        error.response?.data?.message ||
        error.message ||
        'Failed to get linked children'
      );
    }
  }

  async changeChildPassword(childId: number, newPassword: string): Promise<void> {
    try {
      const response = await api.put(`/api/children/${childId}/password`, { newPassword });
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to change child password');
      }
    } catch (error: any) {
      const status = error.response?.status;
      if (status === 409) {
        throw new Error('This child is 16 or older and manages their own credentials');
      }
      throw new Error(error.response?.data?.message || error.message || 'Failed to change child password');
    }
  }
}

export const childManagementService = new ChildManagementService();
export default childManagementService;
